// composables/useAuth.ts
import type { Merchant } from "~/server/db/types";
import type { MeResponse } from "~/server/utils/merchant";

interface AuthState {
  user: MeResponse | null;
  merchant: Merchant | null;
  initialized: boolean;
  loading: boolean;
  lastRefreshTime: number | null;
}

export const useAuth = () => {
  const state = useState<AuthState>("auth", () => {
    // Try to restore state from localStorage on client side
    if (import.meta.client) {
      const savedState = localStorage.getItem("auth_state");
      if (savedState) {
        try {
          const parsed = JSON.parse(savedState);
          // Check if the saved state is not too old (e.g., 30 days)
          const thirtyDays = 30 * 24 * 60 * 60 * 1000;
          if (Date.now() - parsed.timestamp < thirtyDays) {
            return {
              user: parsed.user,
              merchant: parsed.merchant,
              initialized: parsed.initialized,
              loading: false,
              lastRefreshTime: parsed.lastRefreshTime,
            };
          }
        } catch (e) {
          console.error("Error parsing auth state:", e);
        }
      }
    }

    // Default state if no saved state or on server
    return {
      user: null,
      merchant: null,
      initialized: false,
      loading: false,
      lastRefreshTime: null,
    };
  });

  // Watch for state changes and update localStorage
  if (import.meta.client) {
    watch(
      () => state.value,
      (newState) => {
        localStorage.setItem(
          "auth_state",
          JSON.stringify({
            user: newState.user,
            merchant: newState.merchant,
            initialized: newState.initialized,
            timestamp: Date.now(), // Add timestamp
            lastRefreshTime: newState.lastRefreshTime,
          })
        );
      },
      { deep: true }
    );
  }

  const loggedIn = computed(() => !!state.value.user);

  const isAdminUser = computed(() => {
    return state.value.user?.user?.role === "admin";
  });

  const login = async (email: string, password: string) => {
    state.value.loading = true;

    try {
      const response = await $fetch<MeResponse>("/api/auth/login", {
        method: "POST",
        body: { email, password },
      });

      if (response.error) {
        useToastNotification().error("Erreur", response.error.message);
        return;
      }

      if (!response || !response.user) {
        throw new Error("Invalid user data");
      }

      state.value.user = response;
      state.value.merchant = response.merchant;
      state.value.initialized = true;

      useToastNotification().success("Succès", "Connexion réussie");
      navigateTo("/dashboard");
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      state.value.loading = false;
    }
  };

  const clear = async () => {
    state.value.loading = true;
    try {
      await $fetch("/api/auth/logout", { method: "POST" });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      state.value.user = null;
      state.value.initialized = false;
      if (import.meta.client) {
        localStorage.removeItem("auth_state");
        document.cookie =
          "auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
      }
      navigateTo("/");
      state.value.loading = false;
    }
  };

  const refresh = async () => {
    try {
      state.value.loading = true;
      const response = await $fetch<MeResponse>("/api/auth/refresh");

      if (!response || !response.user) {
        state.value.user = null;
        state.value.initialized = true;
        if (import.meta.client) {
          localStorage.removeItem("auth_state");
        }
        return;
      }

      state.value.user = response;
      state.value.initialized = true;
      state.value.lastRefreshTime = Date.now();
    } catch (error) {
      console.error("Refresh error:", error);
      state.value.user = null;
      state.value.initialized = true;
      if (import.meta.client) {
        localStorage.removeItem("auth_state");
      }
      throw error; // Re-throw to be caught by the middleware
    } finally {
      state.value.loading = false;
    }
  };

  const merchant = computed(() => {
    if (!state.value.user?.merchant) {
      return null;
    }
    return {
      ...state.value.user.merchant,
      notification_preferences: state.value.user.merchant
        .notification_preferences
        ? JSON.parse(
            state.value.user.merchant.notification_preferences as string
          )
        : null,
    };
  });

  const needsRefresh = computed(() => {
    if (!state.value.lastRefreshTime) {
      console.log("No last refresh time, refresh needed");
      return true;
    }

    const fiveMinutes = 10 * 60 * 1000;
    return Date.now() - state.value.lastRefreshTime > fiveMinutes;
  });

  return {
    user: computed(() => state.value.user),
    merchant: merchant,
    isAdminUser: isAdminUser,
    loggedIn: loggedIn,
    loading: computed(() => state.value.loading),
    initialized: computed(() => state.value.initialized),
    env: computed(() => state.value.user?.env),
    login,
    clear,
    refresh,
    needsRefresh,
  };
};
